import { tracked } from '@glimmer/tracking';
import type MetricsService from 'garaje/services/metrics';

/**
 * Interface for a search task that can be performed.
 * This allows flexibility in the task signature while ensuring type safety.
 */
interface SearchTask {
  perform(query: string, skipLengthCheck?: boolean): unknown;
}

/**
 * Shared debounced search behavior for searchable lists (invites, entries, etc.)
 *
 * Provides common search functionality including:
 * - Dual state management (input vs actual query)
 * - Min search length validation
 * - "Press ENTER to search" hint logic
 * - Clear search functionality
 * - Metrics tracking
 *
 * Note: ember-concurrency tasks must still live on the controller/component
 * for proper lifecycle management. This manager provides the validation and
 * state update logic that the task needs to perform its search.
 */

export const MIN_SEARCH_LENGTH = 3;

export interface DebouncedSearchManagerOptions {
  /** Function to get the current query value */
  getCurrentQuery: () => string;
  /** Function to set the query value */
  setQuery: (value: string) => void;
  /** Optional function to set the page (default: 1) */
  setPage?: (page: number) => void;
  /** Metrics service for tracking search events */
  metrics: MetricsService;
  /** Name of the metrics event to track */
  eventName: string;
  /** Minimum search length before auto-search (default: 3) */
  minSearchLength?: number;
}

export class DebouncedSearchManager {
  /**
   * Internal tracked property for the search input field value.
   * This allows the input to update immediately while the actual search is debounced.
   */
  @tracked _searchQuery: string | null = null;

  /**
   * Tracked property to control visibility of result counts.
   * Set to false when search starts, true when results load.
   */
  @tracked showCount = true;

  private getCurrentQuery: () => string;
  private setQuery: (value: string) => void;
  private setPage?: (page: number) => void;
  private metrics: MetricsService;
  private eventName: string;

  readonly minSearchLength: number;

  constructor(options: DebouncedSearchManagerOptions) {
    this.getCurrentQuery = options.getCurrentQuery;
    this.setQuery = options.setQuery;
    this.setPage = options.setPage;
    this.metrics = options.metrics;
    this.eventName = options.eventName;
    this.minSearchLength = options.minSearchLength ?? MIN_SEARCH_LENGTH;
  }

  /**
   * Getter for search query - returns internal state or falls back to controller's query.
   * This ensures the input field shows the latest value even during debounce.
   */
  get searchQuery(): string {
    return this._searchQuery !== null ? this._searchQuery : this.getCurrentQuery();
  }

  /**
   * Setter for search query - updates the internal input state.
   */
  set searchQuery(value: string) {
    this._searchQuery = value;
  }

  /**
   * Returns true if there's an active search query that can be cleared.
   */
  get showClearButton(): boolean {
    return this.getCurrentQuery().length > 0;
  }

  /**
   * Returns true if the user has entered a search query that is too short
   * and hasn't pressed enter yet.
   */
  get showSearchHint(): boolean {
    const query = this.searchQuery;
    return query.length > 0 && query.length < this.minSearchLength && query !== this.getCurrentQuery();
  }

  /**
   * Validates whether a search should proceed.
   * Used by the controller's restartable task before executing the search.
   *
   * @param query - The query string to search for
   * @param skipLengthCheck - If true, bypasses minimum length validation (e.g., on Enter key)
   * @returns true if search should proceed, false otherwise
   */
  shouldSearch(query: string, skipLengthCheck: boolean = false): boolean {
    const trimmedQuery = query.trim();
    if (this.getCurrentQuery() === trimmedQuery) return false;
    if (!skipLengthCheck && trimmedQuery.length < this.minSearchLength) return false;

    return true;
  }

  /**
   * Executes the search by updating state, calling callbacks, and tracking metrics.
   * Called by the controller's task after the debounce timeout.
   *
   * @param query - The query string to search for
   */
  executeSearch(query: string): void {
    const trimmedQuery = query.trim();
    this.showCount = false;
    this.setQuery(trimmedQuery);

    // Using the trimmed query here ensures the UI always displays what's actually being searched
    this._searchQuery = trimmedQuery;

    // Reset to first page when searching
    if (this.setPage) {
      this.setPage(1);
    }

    this.metrics.trackEvent(this.eventName, { search_value: trimmedQuery });
  }

  /**
   * Handles form submission (e.g., user presses Enter key).
   * Triggers search even if query is shorter than minimum length.
   *
   * @param event - The form submit event
   * @param mutateQueryTask - The controller's restartable search task
   */
  handleFormSubmit(event: Event, mutateQueryTask: SearchTask): void {
    event.preventDefault();
    const trimmedQuery = this.searchQuery.trim();
    if (this.getCurrentQuery() !== trimmedQuery) {
      void mutateQueryTask.perform(trimmedQuery, true);
    }
  }

  /**
   * Handles input field changes with debounced search.
   * If query is long enough, triggers search. If empty, clears search.
   *
   * @param value - The new input value
   * @param mutateQueryTask - The controller's restartable search task
   */
  handleInputChange(value: string, mutateQueryTask: SearchTask): void {
    this.searchQuery = value;

    if (value.trim()) {
      void mutateQueryTask.perform(value.trim(), false);
    } else {
      this.clearSearch();
    }
  }

  /**
   * Clears the search query and resets the search state.
   */
  clearSearch(): void {
    this.showCount = !this.getCurrentQuery();
    this.resetSearchQueries();
  }

  /**
   * Resets both the controller query and internal search query to empty.
   */
  resetSearchQueries(): void {
    this.setQuery('');
    this.searchQuery = '';
  }
}
